import { db } from "@/indexedDB/db";
import { queryClient } from "@/queryClient";
import { createLogger } from "@/utils/debug";

const log = createLogger("chatTeardown");

/**
 * Remove EVERYTHING a chat left behind locally, except the chat row itself
 * (callers pair this with removeChatFromLocalDB / their own row handling).
 *
 * Before this helper, every teardown site removed only messages + the chat
 * row, orphaning pinnedMessages, media/file/voice attachment rows, links —
 * and, worse, the DECRYPTED attachment blobs in OPFS (files ≥5 MB are stored
 * as plaintext). Account deletion makes this path hot: torn-down chats
 * (note-to-self, solo Architect chats, last-participant chats) arrive as
 * `chat` Delete events and in removedChatIds.
 *
 * ORDER MATTERS: attachment ids are collected and their OPFS/content blobs
 * deleted BEFORE the rows — a bare where("chatId").delete() would strand the
 * plaintext on disk with nothing left pointing at it.
 *
 * Best-effort per step; never throws.
 */
export async function removeChatDataFromLocalDB(chatId: string): Promise<void> {
  try {
    // The attachment tables' primary key `id` IS the attachment id.
    const [mediaIds, fileIds, voiceIds] = await Promise.all([
      db.mediaAttachments.where("chatId").equals(chatId).primaryKeys(),
      db.fileAttachments.where("chatId").equals(chatId).primaryKeys(),
      db.voiceAttachments.where("chatId").equals(chatId).primaryKeys(),
    ]);

    const opfsIds = [...mediaIds, ...fileIds, ...voiceIds] as string[];
    if (opfsIds.length) {
      const { deleteAttachmentContent } = await import(
        "@/lib/storage/attachmentStorage"
      );
      await Promise.all(
        opfsIds.map(async (id) => {
          try {
            await deleteAttachmentContent(id);
          } catch (err) {
            log.warn("OPFS content delete failed", { id, err });
          }
        }),
      );
    }

    await Promise.all([
      db.messages.where("chatId").equals(chatId).delete(),
      db.pinnedMessages.where("chatId").equals(chatId).delete(),
      db.mediaAttachments.where("chatId").equals(chatId).delete(),
      db.fileAttachments.where("chatId").equals(chatId).delete(),
      db.voiceAttachments.where("chatId").equals(chatId).delete(),
      db.links.where("chatId").equals(chatId).delete(),
    ]);
  } catch (err) {
    log.warn("chat data sweep failed", { chatId, err });
  }

  // Per-chat React Query caches would otherwise survive the chat.
  try {
    queryClient.removeQueries({ queryKey: ["chat-members", chatId] });
    queryClient.removeQueries({
      queryKey: ["public-group-users-requests", chatId],
    });
  } catch {
    // best-effort
  }
}
